import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useOutsideDetect } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import styled from '@mint-lib/styled';
import { z } from 'zod';

import { Box, Popover } from '@myn/mui';

import { usePreparedBuilderSchema } from '../../../../hooks/useGetBuilderSchemaData.js';
import type { TechCostColumn } from '../../../../types/buyingPlan.js';
import { CustomDimension } from '../../../../types/campaignBuilder.js';
import PreEditModal from '../CustomColumnForm/PreEditModal.jsx';
import TechCostFormContent from './TechCostFormContent.jsx';

const defaultValues: TechCostColumn = {
  name: '',
  techCostPercent: null,
  isAutoCalculationEnabled: false,
};

const getSchema = (
  items: ReturnType<typeof usePreparedBuilderSchema>['otherTechCosts'],
  currentName: string | undefined,
) =>
  z
    .object({
      name: z.string().min(1, 'Name is a required field'),
      techCostPercent: z.number().nullable().optional(),
      isAutoCalculationEnabled: z.boolean(),
    })
    .superRefine((data, ctx) => {
      const isCurrentName = currentName === data.name;
      const isUnique = !items
        .map((item) => item.name.trim())
        .includes(String(data.name?.trim()));

      if (!isUnique && !isCurrentName) {
        ctx.addIssue({
          code: 'custom',
          path: ['name'],
          message: `${data.name} is not a unique name. Change the name.`,
        });
      }

      if (data.isAutoCalculationEnabled && !data.techCostPercent) {
        ctx.addIssue({
          code: 'custom',
          path: ['techCostPercent'],
          message: 'The value is required',
        });
      }
    });

export type Props = {
  isOpen: boolean;
  isEditMode?: boolean;
  editableValues: TechCostColumn | null;
  onSubmit: (
    data: TechCostColumn,
    onReset: () => void,
    onFailed: () => void,
  ) => void;
  onEdit: (
    data: TechCostColumn,
    onReset: () => void,
    onFailed: () => void,
  ) => void;
  onDelete: (onReset: () => void, onFailed: () => void) => void;
  onCancel: () => void;
  className?: string;
  preEditMode: boolean;
  setPreEditMode: (value: boolean) => void;
  anchorEl: HTMLElement | null;
  setIsFormOpen: (value: boolean) => void;
  currentDimension?: CustomDimension | null;
};

const CustomColumnForm = ({
  isOpen = false,
  isEditMode = false,
  editableValues,
  onSubmit,
  onDelete,
  onEdit,
  onCancel,
  className,
  preEditMode,
  setPreEditMode,
  anchorEl,
  setIsFormOpen,
  currentDimension,
}: Props) => {
  const { t } = useTranslation('@myn/planning');

  const [isWarningOpen, setIsWarningOpen] = useState<boolean>(false);
  const [isSuccessfullyRemoved, setIsSuccessfullyRemoved] =
    useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { otherTechCosts } = usePreparedBuilderSchema();
  const popoverRef = useRef<HTMLFormElement>(null);

  const {
    handleSubmit: submit,
    control,
    reset,
    formState,
    watch,
  } = useForm<TechCostColumn>({
    defaultValues,
    resolver: zodResolver(getSchema(otherTechCosts, editableValues?.name)),
  });

  const isAutoCalculationEnabled = watch('isAutoCalculationEnabled');

  const isSaveDisabled = !formState.isDirty;

  useEffect(() => {
    if (editableValues && !preEditMode) {
      reset(editableValues);
      return;
    }

    reset(defaultValues);
  }, [reset, editableValues, preEditMode]);

  useEffect(() => {
    if (!isOpen) {
      setIsSuccessfullyRemoved(false);
    }
  }, [isOpen]);

  const handleSubmit = (data: TechCostColumn) => {
    const adjustedTechCostPercent = isAutoCalculationEnabled
      ? data.techCostPercent
      : null;

    const dataForRequest = {
      ...data,
      techCostPercent: adjustedTechCostPercent,
    };

    const onReset = () => {
      setIsSubmitting(false);
      reset(defaultValues);
    };

    const onFailed = () => {
      setIsSubmitting(false);
    };

    setIsSubmitting(true);

    if (!isEditMode) {
      onSubmit(dataForRequest, onReset, onFailed);
    } else {
      onEdit(dataForRequest, onReset, onFailed);
    }
  };

  const handleWarningToggle = useCallback(() => {
    setIsWarningOpen((state) => !state);
  }, []);

  const handleDelete = useCallback(() => {
    const onReset = () => {
      reset(defaultValues);
      setIsSuccessfullyRemoved(true);
      setIsSubmitting(false);
      setIsWarningOpen(false);
    };

    const onSuccess = () => {
      onReset();
      setIsFormOpen(false);

      return enqueueSnackbar({
        variant: 'success',
        title: t('Success'),
        subTitle: t('{{dimensionName}} was deleted successfully.', {
          dimensionName: currentDimension?.name,
        }),
        autoHideDuration: 3000,
        removable: true,
      });
    };

    const onFailed = () => {
      setIsSubmitting(false);

      return enqueueSnackbar({
        variant: 'error',
        title: t('Error'),
        subTitle: t('{{dimensionName}} can not be deleted.', {
          dimensionName: currentDimension?.name,
        }),
        autoHideDuration: 3000,
        removable: true,
      });
    };

    setIsSubmitting(true);

    onDelete(onSuccess, onFailed);
  }, [onDelete]);

  const handleCancel = useCallback(() => {
    reset(defaultValues);
    onCancel();
  }, [reset, onCancel]);

  useOutsideDetect(popoverRef, isOpen, handleCancel);

  if (!isOpen) {
    return null;
  }

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
    >
      <Box
        ref={popoverRef}
        sx={{
          height: 'fit-content',
        }}
      >
        {preEditMode ? (
          <PreEditModal
            handleCancel={onCancel}
            handleDelete={handleDelete}
            setPreEditMode={setPreEditMode}
            handleWarningToggle={handleWarningToggle}
            isWarningOpen={isWarningOpen}
          />
        ) : (
          <Form
            aria-label="form"
            // @ts-expect-error TODO: fix the type of handleSubmit
            onSubmit={submit(handleSubmit as SubmitHandler<TechCostColumn>)}
            className={className}
            $isHidden={isSuccessfullyRemoved || isWarningOpen}
          >
            <TechCostFormContent
              control={control}
              handleCancel={handleCancel}
              isDisabled={isSaveDisabled}
              isSubmitting={isSubmitting}
              isAutoCalculationEnabled={isAutoCalculationEnabled}
            />
          </Form>
        )}
      </Box>
    </Popover>
  );
};

const Form = styled('form')<{ $isHidden?: boolean }>`
  display: flex;
  width: 320px;
  padding: ${({ theme }) => theme.spacing(1)};
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 100%;
`;

export default memo(CustomColumnForm);
